import type { BlocksContent } from '@strapi/blocks-react-renderer';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { PricingSlot } from './PricingSlot';
import type { CompactPricingCardConfig, CompactPricingResolved } from './types';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { resolvePrice } from '@/lib/pricing/resolvePrice';
import { hasRichTextContent } from '@/lib/render/hasRichTextContent';
import { imageAlt } from '@/lib/render/imageAlt';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { substituteHref } from '@/lib/render/substituteHref';
import { useRuntimeConfig } from '@/lib/runtime-config/RuntimeConfigProvider';

function SingleCard({
  header,
  price,
  fixedPrice,
  pricePrefix,
  ctaUrl,
  onCtaClick,
}: {
  header: string;
  price: string | undefined;
  /** Literal price, shown when `price` did not resolve (SH-415). */
  fixedPrice?: BlocksContent | undefined;
  pricePrefix: string;
  ctaUrl?: string;
  onCtaClick?: () => void;
}) {
  return (
    <PricingSlot
      ctaUrl={ctaUrl}
      onClick={onCtaClick}
      className="bg-secondary-lighter flex flex-1 flex-col items-center justify-center gap-2 rounded-xl px-3 py-5 text-center"
    >
      <p className="text-primary m-0 text-sm leading-none font-bold uppercase md:text-base">
        {header}
      </p>
      {price ? (
        <p className="text-primary-dark m-0 text-base leading-none font-normal whitespace-nowrap">
          {pricePrefix} {price}
        </p>
      ) : (
        fixedPrice && (
          // A div, not a p: the richtext renders its own block-level
          // paragraphs. No `pricePrefix` — the editor's text is complete.
          <div className="text-primary-dark text-base leading-none font-normal [&_p]:m-0">
            <RichText nodes={fixedPrice} />
          </div>
        )
      )}
    </PricingSlot>
  );
}

function CompactPricingCardC({ card }: { card: CompactPricingCardConfig }) {
  const {
    entities,
    imageUrl,
    substitutePlaceholders: sub,
  } = useRenderContext();
  const t = useTranslations('compactPricing');
  const { brand: domainKey } = useRuntimeConfig();
  const price1 =
    resolvePrice(entities.location, card.pricePlaceholder, domainKey) ??
    undefined;
  const price2 = card.secondPricePlaceholder
    ? (resolvePrice(
        entities.location,
        card.secondPricePlaceholder,
        domainKey,
      ) ?? undefined)
    : undefined;
  // Literal fallbacks, used per slot when the placeholder is empty or
  // resolves to nothing (SH-415).
  const fixedPrice1 = hasRichTextContent(card.fixedPrice)
    ? card.fixedPrice
    : undefined;
  const fixedPrice2 = hasRichTextContent(card.secondFixedPrice)
    ? card.secondFixedPrice
    : undefined;

  // CMS text fields carry `{location.*}` / `{city.*}` tokens (e.g.
  // `{location.matelsoPhoneLabel}` in a badge or description). Nothing
  // upstream substitutes them — `resolveSync` is identity — so each field
  // has to go through the render context here, like every other widget.
  const badge = card.badge ? sub(card.badge) : undefined;
  const planHeader = sub(card.planHeader);
  const description = card.description ? sub(card.description) : undefined;
  const secondPlanHeader = card.secondPlanHeader
    ? sub(card.secondPlanHeader)
    : undefined;
  const ctaUrl = substituteHref(card.ctaUrl, sub) || undefined;
  const secondCtaUrl = substituteHref(card.secondCtaUrl, sub) || undefined;

  const hasTwoCards = Boolean(secondPlanHeader && (price2 || fixedPrice2));
  const imgSrc = imageUrl(card.image);
  // The card's header photo is content, not decoration: editors name it on
  // the media itself.
  const imgAlt = sub(imageAlt(card.image));

  const handleCtaClick = (label: string) => () =>
    pushCmsEventTracking(card.tracking, { label });

  if (hasTwoCards) {
    return (
      <div className="relative pt-5">
        {badge && (
          <span className="text-primary-dark absolute top-5 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-[20px] bg-[var(--color-light-blue)] px-[15px] py-[6px] text-sm font-semibold whitespace-nowrap uppercase">
            {badge}
          </span>
        )}
        <div className="flex gap-2 md:gap-[15px]">
          <SingleCard
            header={planHeader}
            price={price1}
            fixedPrice={fixedPrice1}
            pricePrefix={t('pricePrefix')}
            ctaUrl={ctaUrl}
            onCtaClick={handleCtaClick(planHeader)}
          />
          <SingleCard
            header={secondPlanHeader!}
            price={price2}
            fixedPrice={fixedPrice2}
            pricePrefix={t('pricePrefix')}
            ctaUrl={secondCtaUrl}
            onCtaClick={handleCtaClick(secondPlanHeader!)}
          />
        </div>
      </div>
    );
  }

  return (
    <PricingSlot
      ctaUrl={ctaUrl}
      onClick={handleCtaClick(planHeader)}
      className="relative block rounded-2xl bg-[var(--color-secondary-lighter)] lg:rounded-t-2xl lg:rounded-b-none"
    >
      {badge && (
        <span className="text-primary-dark absolute top-0 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-[20px] bg-[var(--color-light-blue)] px-4 py-1.5 text-sm font-semibold whitespace-nowrap uppercase">
          {badge}
        </span>
      )}
      {imgSrc && (
        <div className="relative hidden aspect-[349/131] w-full lg:block">
          <Image
            src={imgSrc}
            alt={imgAlt}
            fill
            className="rounded-t-2xl border border-white object-cover"
          />
        </div>
      )}
      <div className="flex flex-col items-center gap-1 px-6 py-6">
        <p className="text-primary m-0 text-sm font-bold uppercase">
          {planHeader}
        </p>
        {description && (
          <p className="text-foreground m-0 text-center text-base font-medium lg:text-2xl">
            {description}
          </p>
        )}
        {price1 ? (
          <p className="text-primary-dark m-0 text-[32px] font-extrabold lg:text-[50px]">
            {t('pricePrefix')} {price1}
          </p>
        ) : (
          fixedPrice1 && (
            <div className="text-primary-dark text-[32px] font-extrabold lg:text-[50px] [&_p]:m-0">
              <RichText nodes={fixedPrice1} />
            </div>
          )
        )}
      </div>
    </PricingSlot>
  );
}

export function CompactPricingComponentC({
  resolved,
}: {
  resolved: CompactPricingResolved;
}) {
  const cards = resolved.cards ?? [];

  if (cards.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      {cards.map((card, i) => (
        <CompactPricingCardC key={`${card.planHeader}-${i}`} card={card} />
      ))}
    </div>
  );
}
